/**
 * Transaction process graph for monthly subscriptions (Abo):
 *   - cowork24-abo
 *
 * Flow: the customer REQUESTS the subscription — the amount is only authorized
 * (on-session, 3DS capable). The PROVIDER must accept: only the acceptance
 * captures the money (modeled on default-booking). Without a reaction the
 * request expires after 6 days and the hold is released.
 *
 * Cycle 1 then runs through Sharetribe; renewals are billed by a Stripe
 * Billing subscription (Sharetribe supports only one PaymentIntent per
 * transaction) — the transitions from the ACTIVE state onward are
 * "shell" transitions without Stripe actions: the Stripe webhook receiver
 * (server/api/stripe-webhook.js) and the reconcile cron translate Stripe
 * events into operator transitions via the Integration API so that
 * inbox/status/emails stay truthful.
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer subscribes, a transaction is created with the initial
  // request-payment transition. At this transition a PaymentIntent is
  // created by Marketplace API. After this transition, the actual payment
  // must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // This CONFIRMS (authorizes) the payment — the money is held, not captured.
  // Capturing happens only when the provider accepts.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process
  // (by default 15min) the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // ---- Provider approval ----
  // Provider accepts: captures the authorized payment and starts the subscription.
  ACCEPT: 'transition/accept',
  // Provider declines: releases the hold (full refund).
  DECLINE: 'transition/decline',
  // Customer withdraws the request before the provider answered.
  CUSTOMER_WITHDRAW: 'transition/customer-withdraw',
  // No provider reaction within 6 days: release the hold before the card
  // authorization (~7 days) lapses on its own.
  EXPIRE: 'transition/expire',

  // Scheduled transition (24h after purchase): releases the month-1 payout
  // and moves the subscription to its long-running ACTIVE state.
  ACTIVATE: 'transition/activate',

  // Operator escape hatch within the first 24h: full refund of month 1.
  OPERATOR_CANCEL: 'transition/operator-cancel',

  // ---- Shell transitions driven by the Stripe webhook / reconcile cron ----
  // A monthly renewal invoice was paid (Stripe Billing).
  CYCLE_PAID: 'transition/cycle-paid',
  // Advance warning before the next charge — sends the customer an e-mail with
  // a cancellation link. Re-entrant (active → active), like CYCLE_PAID.
  UPCOMING_CHARGE: 'transition/upcoming-charge',
  // A renewal charge failed — subscription enters dunning.
  PAYMENT_FAILED: 'transition/payment-failed',
  // A Stripe Smart Retry succeeded — subscription is active again.
  RETRY_SUCCEEDED: 'transition/retry-succeeded',
  // Dunning exhausted — Stripe cancelled the subscription.
  CANCEL_DUNNING: 'transition/cancel-dunning',
  // Subscription ended (customer/provider cancelled at period end).
  CANCEL: 'transition/cancel',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  PURCHASED: 'purchased',
  ACTIVE: 'active',
  PAST_DUE: 'past-due',
  CANCELLED: 'cancelled',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'cowork24-abo/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},

    // Waiting for the provider: money is held on the customer's card.
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.ACCEPT]: states.PURCHASED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.CUSTOMER_WITHDRAW]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},

    [states.PURCHASED]: {
      on: {
        [transitions.ACTIVATE]: states.ACTIVE,
        [transitions.OPERATOR_CANCEL]: states.CANCELLED,
      },
    },

    [states.ACTIVE]: {
      on: {
        [transitions.CYCLE_PAID]: states.ACTIVE,
        [transitions.UPCOMING_CHARGE]: states.ACTIVE,
        [transitions.PAYMENT_FAILED]: states.PAST_DUE,
        [transitions.CANCEL]: states.CANCELLED,
      },
    },

    [states.PAST_DUE]: {
      on: {
        [transitions.RETRY_SUCCEEDED]: states.ACTIVE,
        [transitions.CANCEL_DUNNING]: states.CANCELLED,
      },
    },

    [states.CANCELLED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
//
// NOTE: UPCOMING_CHARGE is deliberately absent. The ActivityFeed keys its copy
// on the state a transition leads to (TransactionPage.ActivityFeed.<process>.<state>),
// so a re-entry into ACTIVE would just repeat "Das Abo ist aktiv" once per
// billing cycle. The pre-charge warning is an e-mail event, not a milestone.
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.ACCEPT,
    transitions.DECLINE,
    transitions.CUSTOMER_WITHDRAW,
    transitions.EXPIRE,
    transitions.ACTIVATE,
    transitions.OPERATOR_CANCEL,
    transitions.CYCLE_PAID,
    transitions.PAYMENT_FAILED,
    transitions.RETRY_SUCCEEDED,
    transitions.CANCEL_DUNNING,
    transitions.CANCEL,
  ].includes(transition);
};

// The Abo process has no reviews in v1.
export const isCustomerReview = () => false;
export const isProviderReview = () => false;

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT].includes(transition);
};

// Check when transaction is completed. A subscription is "completed"
// once it has ended for good.
// NOTE: OPERATOR_CANCEL is intentionally NOT here — it is a refund (see
// isRefunded), and the order breakdown checks isCompleted first, so listing it
// would show the provider "total received" instead of "total refunded".
export const isCompleted = transition => {
  return [transitions.CANCEL, transitions.CANCEL_DUNNING].includes(transition);
};

// Check when transaction is refunded (subscription never started)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  return [
    transitions.EXPIRE_PAYMENT,
    transitions.DECLINE,
    transitions.CUSTOMER_WITHDRAW,
    transitions.EXPIRE,
    transitions.OPERATOR_CANCEL,
  ].includes(transition);
};

// The provider must act: a request is waiting with money held on the
// customer's card, and the hold lapses after 6 days.
export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
export const statesNeedingCustomerAttention = [states.PAST_DUE];
